<script setup lang="ts">
import type { ChooserEntry } from '#layers/access/types/auth-providers/define';
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';
import { useColorMode } from '#imports';
import { resolveProviderDisplay } from '#layers/access/utils/auth-providers/registry';

interface Props {
	entry: ChooserEntry;
	loading?: boolean;
	disabled?: boolean;
	testid?: string;
	/** Swap the label to "Sign up with {provider}" (vs default "Continue with"). */
	signupMode?: boolean;
}

const { entry, signupMode = false } = defineProps<Props>();
defineEmits<{ click: [entry: ChooserEntry] }>();

const i18n = useI18n();
const colorMode = useColorMode();

// Resolve name + icon in one pass: the button needs both, so the combined
// resolver reads the provider record once instead of twice.
const display = computed(() =>
	resolveProviderDisplay(entry, entry.def, colorMode.value === 'dark', i18n),
);

const label = computed(() =>
	i18n.t(signupMode ? 'auth.signup.sign_up_with' : 'auth.login.continue_with', { provider: display.value.displayName }),
);

// Outline variant tints the icon via a CSS var (the only seam Nuxt UI exposes
// for per-instance icon color).
const brand = computed(() => {
	const color = display.value.iconColor;
	if (!color) return { style: undefined, ui: undefined };

	return {
		style: { '--brand-icon-color': color },
		ui: { leadingIcon: '[color:var(--brand-icon-color)]' },
	};
});
</script>

<template>
	<DButton
		:icon="display.iconName"
		color="neutral"
		variant="outline"
		size="xl"
		block
		class="gap-2"
		:style="brand.style"
		:ui="brand.ui"
		:loading
		:disabled
		:label
		:data-testid="testid"
		@click="$emit('click', entry)"
	/>
</template>
